import dayjs from 'dayjs';
import React, { useState } from 'react';
import Cookies from 'js-cookie';
import cookies from 'next-cookies';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Box,
  FormBanner,
  FormBannerType,
  Grid,
  OParagraph,
  OTitle,
  PrimaryButton,
  SecondaryButton,
  defaultColors,
  GridItem,
} from '@lifechurch/react-ion';
import TableBody from '@components/Table/TableBody';
import { useServicesContext } from '@lib/servicesContext';
import { RangePicker } from '@components/RangePicker';
import { CURRENT_ORGANIZATION_ONBOARDING, SERVICES_CONNECTION } from '@lib/queries';
import { serviceTimeIsUpcoming } from '@components/Utils';
import ServiceTime from '@components/ServiceTime';
import {
  AdminServicesConnectionQuery,
  AdminServicesConnectionQueryVariables,
  CurrentOrganizationOnboardingQuery,
  CurrentOrganizationOnboardingQueryVariables,
} from '@generated/types';
import { OnboardingStepKeys } from '@components/Onboarding/ChecklistInProgress/ChecklistItems/ChecklistItem';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { DEFAULT_LOCALE } from '@utils/constants';
import { useTranslation } from 'react-i18next';

interface PageProps {
  servicesInfoDismissed: boolean;
}

const Services: NextPage<PageProps> = ({ servicesInfoDismissed }) => {
  const { t } = useTranslation(['services', 'common']);
  const { from, to, updateRange } = useServicesContext();
  const [isServicesInfoDismissed, setServicesInfoDismissed] = useState(servicesInfoDismissed);
  const { data, loading } = useQuery<
    AdminServicesConnectionQuery,
    AdminServicesConnectionQueryVariables
  >(SERVICES_CONNECTION, {
    variables: {
      from,
      to,
    },
  });
  const [currentServices] = useLazyQuery<
    AdminServicesConnectionQuery,
    AdminServicesConnectionQueryVariables
  >(SERVICES_CONNECTION);

  const { data: stepData } = useQuery<
    CurrentOrganizationOnboardingQuery,
    CurrentOrganizationOnboardingQueryVariables
  >(CURRENT_ORGANIZATION_ONBOARDING);

  const addServiceTimeStep = stepData?.currentOrganization?.onboardingSteps?.find(
    step => step.key === OnboardingStepKeys.SERVICE
  );
  const addServiceContentStep = stepData?.currentOrganization?.onboardingSteps?.find(
    step => step.key === OnboardingStepKeys.CONTENT
  );
  // If the step isn't present, assume it is complete
  const serviceTimeStepComplete = addServiceTimeStep?.completed ?? true;
  const serviceContentStepComplete = addServiceContentStep?.completed ?? true;

  const updateDateRange = (from?: string, to?: string): void => {
    currentServices({
      variables: {
        from,
        to,
      },
    });
    from && to && updateRange(from, to);
  };

  const dismissServicesInfo = (): void => {
    Cookies.set('SERVICES_INFO_DISMISSED', 'true', { expires: 3000 });
    setServicesInfoDismissed(true);
  };

  if (loading)
    return (
      <>
        <OTitle marginBlockEnd={48}>{t('services')}</OTitle>
      </>
    );
  if (
    data &&
    data.currentOrganization &&
    data.currentOrganization.servicesConnection &&
    data.currentOrganization.servicesConnection.aggregate.count > 0
  ) {
    const {
      currentOrganization: {
        servicesConnection: { services },
      },
    } = data;
    return (
      <>
        <OTitle marginBlockEnd={32}>{t('services')}</OTitle>
        {!isServicesInfoDismissed && (
          <Box marginBlockEnd={5}>
            <FormBanner onDismiss={dismissServicesInfo} type={FormBannerType.Information} withIcon>
              <OParagraph>{t('services_require_content')}</OParagraph>
            </FormBanner>
          </Box>
        )}
        <Box display={'flex'} justifyContent={'space-between'} marginBlockEnd={5}>
          <RangePicker
            data-testid='datepicker'
            from={new Date(from)}
            fromMonth={new Date(dayjs().subtract(1, 'year').format())}
            needPastDates
            onClick={updateDateRange}
            to={new Date(to)}
          />
          <Link as='/service/new' href='/service/[id]'>
            <SecondaryButton>{t('add_service_time')}</SecondaryButton>
          </Link>
        </Box>
        <TableBody testId='servicesTable'>
          <Grid gridTemplateColumns='repeat(12, 1fr)'>
            {services.length > 0 ? (
              services.map(service => (
                <ServiceTime
                  isEditable={serviceTimeIsUpcoming(service.scheduleTime)}
                  isOnboarding={!serviceContentStepComplete}
                  key={service.id || undefined}
                  service={service}
                />
              ))
            ) : (
              <GridItem gridColumn='span 12'>
                <OParagraph
                  color={defaultColors.gray50}
                  data-testid='servicesRange-empty'
                  m={'0 auto'}
                  p={'16px 16px'}
                  textAlign='center'
                >
                  {t('no_services_within_range')}
                </OParagraph>
              </GridItem>
            )}
          </Grid>
        </TableBody>
      </>
    );
  } else {
    return (
      <>
        <OTitle marginBlockEnd={48}>{t('services')}</OTitle>
        <Box data-testid='noServices' display='flex' flexDirection='column'>
          <Box margin='0 auto'>
            <img
              alt='Services'
              data-testid='servicesImage'
              height={142}
              src={`/admin/static/services_image.png`}
              width={220}
            />
          </Box>
          <Box
            margin='0 auto'
            maxWidth='488px'
            paddingBlockEnd='24px'
            paddingBlockStart='16px'
            paddingInlineEnd='0'
            paddingInlineStart='0'
            textAlign='center'
          >
            <OParagraph>{t('service_description')}</OParagraph>
          </Box>
          <Link as='/service/new' href='/service/[id]'>
            <PrimaryButton m={'0 auto'} pulsing={!serviceTimeStepComplete}>
              {t('add_service_time')}
            </PrimaryButton>
          </Link>
        </Box>
      </>
    );
  }
};

export const getServerSideProps: GetServerSideProps = async ctx => {
  return {
    props: {
      backgroundColor: defaultColors.gray5,
      servicesInfoDismissed: cookies(ctx).SERVICES_INFO_DISMISSED === 'true',
      ...(await serverSideTranslations(ctx.locale || DEFAULT_LOCALE, ['common', 'services'])),
    },
  };
};

export default Services;
